import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import "../sass/main.scss";
import axios from "axios";
import useWindowDimensions from "../hooks/useWindowDimensions.js";
import { SuccessModal } from "./SuccessModal.js";

export const BookNow = ({ country, destination }) => {

  const [isChecked, setIsChecked] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const { width } = useWindowDimensions();
  const [isLoading, setIsLoading] = useState(false);

  const { handleSubmit, register, watch, getValues, setValue } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      country: country || "",
      destination: destination,
      departDate: "",
      duration: "",
      numberTravellers: 0,
      numberRooms: 0,
      budget: "",
      email: "",
      other: "",
      checkTnc: false,
    },
  });

  const countriesToCityMap = {
    India: ["Amritsar", "Delhi, Agra & Jaipur", "Kerala", "Kashmir", "Ladakh", "Other"],
    Bhutan: [],
    SriLanka: ["Yala, Sirigiya & Bentota", "Minneriya, Nurawa Eliya & Waikkal", "Mihinthale, Polonnaruwa, Kandy", "Other"],
    Other: [],
  };

  useEffect(() => {
    if (showSuccessModal) {
      document.body.classList.add('body-no-scroll');
    } else {
      document.body.classList.remove('body-no-scroll');
    }

    // Cleanup function to remove the class when component unmounts
    return () => {
      document.body.classList.remove('body-no-scroll');
    };
  }, [showSuccessModal]);

  const onSubmit = useCallback(
    async (data) => {
      if (data && isChecked) {
        setIsLoading(true);
        try {
          const response = await axios.post(
            "https://wander-server.vercel.app/api/bookNow",
            data
          );
          if (response.data.success) {
            setIsLoading(false);
            setShowSuccessModal(true);
          }
        } catch (e) {
          setIsLoading(false);
          window.alert("There was an error while booking..Please try again");
          console.log("There was an error while booking..Please try again", e);
        }
      }
    },
    [isChecked]
  );

  const cityOptions = useMemo(() => {
    return watch("country") ? countriesToCityMap[getValues("country")] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch("country"), getValues("country")]);

  const increaseTravellers = useCallback(
    () => setValue("numberTravellers", watch("numberTravellers") + 1),
    [watch, setValue]
  );
  const decreaseTravellers = useCallback(
    () => setValue("numberTravellers", watch("numberTravellers") - 1),
    [watch, setValue]
  );

  const increaseRooms = useCallback(
    () => setValue("numberRooms", watch("numberRooms") + 1),
    [watch, setValue]
  );
  const decreaseRooms = useCallback(
    () => setValue("numberRooms", watch("numberRooms") - 1),
    [watch, setValue]
  );

  return (
    <div className="book-now-wrapper">
      <div className="book-now-container">
        {showSuccessModal && (
          <SuccessModal onClose={() => setShowSuccessModal(false)} />
        )}
        <form onSubmit={handleSubmit(onSubmit)} className="container-part-1">
          <div className="inputs-row-4">

            <div key="firstName" className="input-container" style={{ paddingLeft: 0 }}>
              <p>First Name</p>
              <input {...register("firstName", { required: true })} />
            </div>

            <div key="lastName" className="input-container">
              <p>Last Name</p>
              <input {...register("lastName", { required: true })} />
            </div>

            <div key={"date"} className="input-container">
              <p>Departure Date</p>
              <input
                type="date"
                {...register("departDate", { required: true })}
                placeholder="Departure Date"
              />
            </div>

            <div key={"stay"} className="input-container" style={{ paddingRight: 0 }}>
              <p>Length of Stay</p>
              <input {...register("duration", { required: true })} placeholder="E.g. 5 days, 1 week, 10 days.." />
            </div>
          </div>

          <div className="inputs-row-4">

            <div key="country" className="input-container" style={{ paddingLeft: 0 }}>
              <p>Country</p>
              <select
                className="input-container-select"
                {...register("country", { required: true })}
              >
                {Object.keys(countriesToCityMap).map((country) => (
                  <option
                    className="input-container-select-option"
                    value={country}
                  >
                    {country}
                  </option>
                ))}
              </select>
            </div>

            <div key={"destination"} className="input-container">
              <p>Destination</p>
              <select
                className="input-container-select"
                {...register("destination", { required: false })}
              >
                {watch("country") !== "Other" && cityOptions
                  .map((country) => (
                    <option
                      className="input-container-select-option"
                      value={country}
                    >
                      {country}
                    </option>
                  ))}
              </select>
            </div>

            <div key={"numberTravellers"} className="counter-field" >
              <p>No. of Travellers</p>

              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="button-round" onClick={increaseTravellers}>
                  +
                </button>
                <p
                  style={{ margin: "auto 0px" }}
                  {...register("numberTravellers", {
                    required: true,
                    min: 0,
                    minLength: 0,
                  })}
                >
                  {getValues("numberTravellers")}
                </p>
                <button
                  className="button-round"
                  disabled={watch("numberTravellers") === 0}
                  onClick={decreaseTravellers}
                >
                  -
                </button>
              </div>
            </div>

            <div key={"room"} className="counter-field" style={{
              paddingLeft: width < 600 && "1rem",
              paddingRight: 0
            }}>
              <p>No. of Rooms</p>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button onClick={increaseRooms} className="button-round">
                  +
                </button>
                <p
                  style={{ margin: "auto 0px" }}
                  {...register("numberRooms", {
                    required: true,
                    min: 0,
                    minLength: 0,
                  })}
                >
                  {getValues("numberRooms")}
                </p>
                <button
                  disabled={watch("numberRooms") === 0}
                  className="button-round"
                  onClick={decreaseRooms}
                >
                  -
                </button>
              </div>
            </div>
          </div>

          <div className="inputs-row-2">
            <div key={"budget"} className="input-container" style={{ paddingLeft: 0 }}>
              <p>Budget</p>
              <select
                className="input-container-select"
                {...register("budget", { required: true })}
              >
                <option
                  className="input-container-select-option"
                  value="S$ 2000/5000 per person"
                >
                  S$ 2000/5000 per person
                </option>
                <option
                  className="input-container-select-option"
                  value="S$ 5000/10000 per person"
                >
                  S$ 5000/10000 per person
                </option>
                <option
                  className="input-container-select-option"
                  value="S$ 10000/15000 per person"
                >
                  S$ 10000/15000 per person
                </option>
                <option
                  className="input-container-select-option"
                  value="Over S$15000 per person"
                >
                  Over S$15000 per persons
                </option>
                <option
                  className="input-container-select-option"
                  value="Other"
                >
                  Other
                </option>
              </select>
            </div>
            <div key="email" className="input-container" style={{ paddingRight: 0 }}>
              <p>Email</p>
              <input {...register("email", { required: true })} />
            </div>
          </div>

          <div key="name" className="input-container" style={{ width: "100%", paddingLeft: 0 }}>
            <p>Other Requests</p>
            <input {...register("other", { required: false })} placeholder={(watch("country") === "Other" || watch("destination") === "Other") ? "If you chose 'Other' as country or destination, you could let us know where you would wish for WanderLuxe to host your trip, and we'll do the rest!" : ""} />
          </div>
        </form>

        <div className="container-part-2" style={{ paddingLeft: 0 }}
        >
          <div className="checkbox-row" >
            <input
              onClick={(e) => setIsChecked(e.target.checked)}
              type="checkbox"
              value={true}
            />
            <p>
              I agree to my personal information being used in accordance to
              WanderLuxe privacy policy
            </p>
          </div>

        </div>

      </div>
      {
        isLoading ? <div style={{
          minHeight: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          margin: width < 500 && "2rem"
        }}>
          <div className="lazy-loader" />
        </div>
          :
          <button
            onClick={handleSubmit(onSubmit)}
            style={{ margin: width < 500 && "2rem" }}
            className="button-greyish"
          >
            Submit
          </button>}

    </div>
  );
};
